import React from "react";

import {
  Button,
  ButtonProps,
  IconButton,
  IconButtonProps,
  Theme,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

interface HeaderButtonIconProps
  extends Pick<IconButtonProps, "disabled" | "onClick" | "className" | "sx"> {
  Icon: React.ReactNode;
  primary?: boolean;
}

type ButtonBaseProps = Pick<
  ButtonProps,
  | "startIcon"
  | "endIcon"
  | "disabled"
  | "children"
  | "onClick"
  | "className"
  | "sx"
> & { primary?: boolean };

export type HeaderButtonProps = ButtonBaseProps | HeaderButtonIconProps;
const SHARED_STYLES = {
  borderRadius: "8px",
  height: "40px",

  "& svg": {
    height: "16px",
    width: "16px",
  },
};

const getSharedStyles = (theme: Theme) => ({
  ...SHARED_STYLES,
  "&.Mui-disabled": {
    color: theme.palette.shades.neutral[300],
    backgroundColor: theme.palette.shades.neutral[50],
    border: "none",
  },
});

const StyledButton = styled(
  ({ primary, children, ...props }: ButtonBaseProps) => (
    <Button variant={primary ? "contained" : "outlined"} {...props}>
      <span className="header-button__label">{children}</span>
    </Button>
  )
)(({ theme, primary }) => ({
  ...getSharedStyles(theme),
  background: primary ? theme.palette.shades.primary[500] : "transparent",
  fontWeight: 500,
  fontSize: "1rem",
  lineHeight: "18px",
  letterSpacing: "-0.02rem",
  color: primary ? "#fff" : theme.palette.shades.neutral[500],
  border: "none",
  whiteSpace: "nowrap",
  "&:hover": {
    border: "none",
    background: primary
      ? theme.palette.shades.primary[600]
      : theme.palette.shades.ghost[300],
    color: primary ? "#fff" : theme.palette.shades.primary[500],
  },
  boxShadow: "none",
  [theme.breakpoints.down("lg")]: {
    padding: "6px 14px",
    minWidth: 0,
    "& .header-button__label": {
      display: "none",
    },
    "& .MuiButton-startIcon, .MuiButton-endIcon": {
      margin: 0,
    },
  },
}));

const StyledIconButton = styled(({ Icon, ...props }: HeaderButtonIconProps) => (
  <IconButton {...props}>{Icon}</IconButton>
))(({ primary, theme }) => ({
  ...getSharedStyles(theme),
  width: "40px",
  maxWidth: "40px",
  background: primary ? theme.palette.shades.primary[500] : "transparent",
  color: primary ? "#FFF" : theme.palette.shades.neutral[500],
  border: "none",
  "&:hover": {
    border: "none",
    background: primary
      ? theme.palette.shades.primary[600]
      : theme.palette.shades.ghost[300],
    color: primary ? "#FFF" : theme.palette.shades.primary[500],
  },
  "& .MuiTouchRipple-child ": {
    borderRadius: "8px",
  },
}));

const HeaderButton = ({ className, ...props }: HeaderButtonProps) => {
  if ("Icon" in props) {
    return (
      <StyledIconButton
        className={`header-button header-icon-button${
          className ? ` ${className}` : ""
        }`}
        {...props}
      />
    );
  }

  return (
    <StyledButton
      className={`header-button${className ? ` ${className}` : ""}`}
      {...props}
    />
  );
};

export default HeaderButton;
